import { useEffect, useRef } from "react";
import { Users } from "lucide-react";

export default function HalfInningBreakModal({
  game,
  myRole,
  isOnline,
  onReady,
  clock,
  opponentReady,
  myReady,
  onRequestPitcherSub,
  onRequestBatterSub,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) containerRef.current.focus();
  }, []);

  if (!game) return null;

  const state = game.state || {};
  const inning = state.inning || 1;
  const half = state.half || "top";
  const halfLabel = half === "top" ? "Top" : "Bottom";
  const homeScore = state.home_score ?? 0;
  const awayScore = state.away_score ?? 0;

  // Pitching / batting teams this half
  const battingRole = half === "top" ? "away" : "home";
  const pitchingRole = half === "top" ? "home" : "away";

  const myTeamName = myRole ? (game[`${myRole}_team`]?.name || myRole) : null;
  const oppRole = myRole === "home" ? "away" : "home";
  const oppTeamName = myRole ? (game[`${oppRole}_team`]?.name || oppRole) : null;

  const isPitching = myRole && myRole === pitchingRole;
  const canPitcherSub = isOnline && myRole && isPitching && !!onRequestPitcherSub;
  const canBatterSub = isOnline && myRole && !isPitching && !!onRequestBatterSub;

  const inningComplete = half === "top"
    ? `End of Top ${inning}`
    : `End of Bottom ${inning}`;

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Half-inning break — trade cards back and ready up"
      data-testid="half-inning-modal"
      className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onKeyDown={(e) => { if (e.key === "Enter" && !myReady) onReady?.(); }}
    >
      <div className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl flex flex-col w-full max-w-sm overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div>
            <p className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Half-Inning Break</p>
            <p className="font-black text-white">{inningComplete}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-black text-white tabular-nums">
              {awayScore} – {homeScore}
            </p>
            <p className="text-[10px] text-slate-500">
              {game.away_team?.name} · {game.home_team?.name}
            </p>
          </div>
        </div>

        {/* Roster hint */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <div className="flex items-center gap-2 text-slate-500">
            <Users size={12} />
            <span className="text-[10px] font-bold uppercase tracking-widest">Roster</span>
          </div>

          {/* Both teams abbreviated */}
          {(["home", "away"]).map(teamRole => {
            const team = game[`${teamRole}_team`];
            if (!team) return null;
            const order = game.state?.[`${teamRole === "home" ? "home" : "away"}_batter_index`] ?? 0;
            const isPitchingTeam = teamRole === pitchingRole;
            const pitcher = team.players?.find(p => p.position === "P" && !p.subbed_out);
            const starters = (team.batting_order || [])
              .map(id => team.players?.find(p => p.id === id))
              .filter(Boolean);

            return (
              <div key={teamRole} className="rounded-xl border border-slate-800 bg-slate-900/40 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-black text-white">{team.name}</p>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                    isPitchingTeam
                      ? "border-sky-500/30 text-sky-400 bg-sky-500/10"
                      : "border-amber-500/30 text-amber-400 bg-amber-500/10"
                  }`}>
                    {isPitchingTeam ? "Pitching" : "Batting"}
                  </span>
                </div>

                {pitcher && (
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span className="text-[9px] font-bold text-sky-400/70 w-5">P</span>
                    <span className="font-medium">{pitcher.name}</span>
                    {pitcher.year && <span className="text-slate-600">'{String(pitcher.year).slice(-2)}</span>}
                  </div>
                )}

                <div className="space-y-0.5">
                  {starters.slice(0, 9).map((p, i) => (
                    <div key={p.id} className={`flex items-center gap-2 text-[10px] ${i === order % starters.length && !isPitchingTeam ? "text-amber-300" : "text-slate-500"}`}>
                      <span className="text-[9px] w-3 text-slate-700 tabular-nums">{i + 1}</span>
                      <span className="text-[9px] text-slate-600 w-5">{p.position}</span>
                      <span>{p.name}</span>
                      {p.subbed_out && <span className="text-red-500/60 text-[8px]">OUT</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Sub buttons (my team only, online) */}
        {isOnline && myRole && (
          <div className="px-5 pb-2 flex gap-2">
            {canPitcherSub && (
              <button onClick={onRequestPitcherSub}
                className="flex-1 py-1.5 rounded-lg border border-sky-600/30 text-sky-400 text-xs font-bold hover:bg-sky-900/20 transition-colors">
                Pitcher Sub
              </button>
            )}
            {canBatterSub && (
              <button onClick={onRequestBatterSub}
                className="flex-1 py-1.5 rounded-lg border border-amber-600/30 text-amber-400 text-xs font-bold hover:bg-amber-900/20 transition-colors">
                Batter Sub
              </button>
            )}
          </div>
        )}

        {/* Footer: ready status + button */}
        <div className="px-5 pb-4 pt-2 space-y-2 border-t border-slate-800">
          <div className="flex items-center justify-between text-[10px]">
            {opponentReady && !myReady && (
              <span className="text-amber-400 font-bold">{oppTeamName || "Opponent"} ready ✓</span>
            )}
            {clock !== null && clock > 0 && !myReady && (
              <span className={`tabular-nums font-bold ml-auto ${clock <= 5 ? "text-red-400 animate-pulse" : "text-slate-500"}`}>
                Auto-ready in {clock}s
              </span>
            )}
          </div>
          <button
            data-testid="half-inning-ready"
            onClick={onReady}
            disabled={myReady}
            className={`w-full py-2.5 rounded-xl font-black text-sm transition-all ${
              myReady
                ? "bg-green-800/40 text-green-400 border border-green-700/30"
                : "bg-amber-500 hover:bg-amber-400 text-black"
            }`}
          >
            {myReady ? "Ready ✓" : "Ready →"}
          </button>
        </div>
      </div>
    </div>
  );
}
